import React, { useState } from 'react';
import {
  Box,
  Text,
  Button,
  Input,
  FormControl,
  FormLabel,
  FormErrorMessage,
  VStack,
  HStack,
  Heading,
  List,
  ListItem,
  ListIcon,
  Badge,
  SimpleGrid,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useToast,
} from '@chakra-ui/react';
import { CheckCircleIcon } from '@chakra-ui/icons';
import { useQuery } from 'react-query';
import { billingApi, publicApi, PublicPlan } from '../services/api';

const connectorLabel: Record<string, string> = {
  whatsapp: 'WhatsApp', sql: 'Banco SQL', sql_agent: 'Agente Local', s3: 'Amazon S3',
  google_drive: 'Google Drive', google_drive_managed: 'Google Drive',
  sharepoint: 'SharePoint', sharepoint_managed: 'SharePoint',
};
// Nomes amigáveis sem duplicar a variante "managed" (1-clique) do mesmo serviço.
const connectorNames = (types: string[]): string =>
  Array.from(new Set(types.map((c) => connectorLabel[c] || c))).join(', ');

// Validação de CPF/CNPJ (dígitos verificadores) — feedback imediato.
const isValidCPF = (c: string) => {
  if (c.length !== 11 || /^(\d)\1{10}$/.test(c)) return false;
  let s = 0;
  for (let i = 0; i < 9; i++) s += parseInt(c[i]) * (10 - i);
  let d1 = 11 - (s % 11); if (d1 >= 10) d1 = 0;
  if (d1 !== parseInt(c[9])) return false;
  s = 0;
  for (let i = 0; i < 10; i++) s += parseInt(c[i]) * (11 - i);
  let d2 = 11 - (s % 11); if (d2 >= 10) d2 = 0;
  return d2 === parseInt(c[10]);
};
const isValidCNPJ = (c: string) => {
  if (c.length !== 14 || /^(\d)\1{13}$/.test(c)) return false;
  const calc = (len: number) => {
    let sum = 0, pos = len - 7;
    for (let i = len; i >= 1; i--) { sum += parseInt(c[len - i]) * pos--; if (pos < 2) pos = 9; }
    const r = sum % 11;
    return r < 2 ? 0 : 11 - r;
  };
  if (calc(12) !== parseInt(c[12])) return false;
  return calc(13) === parseInt(c[13]);
};
const isValidDoc = (v: string) => {
  const d = v.replace(/\D/g, '');
  if (d.length === 11) return isValidCPF(d);
  if (d.length === 14) return isValidCNPJ(d);
  return false;
};

const PlanCard = ({ plan, selected, onSelect }: { plan: PublicPlan; selected: boolean; onSelect: () => void }) => (
  <Box
    borderWidth="2px"
    borderColor={selected ? 'brand.500' : 'gray.200'}
    bg={selected ? 'brand.50' : 'white'}
    borderRadius="lg"
    p={4}
    cursor="pointer"
    onClick={onSelect}
    transition="all 0.15s"
  >
    <HStack justify="space-between" mb={2}>
      <Text fontWeight="bold">{plan.label}</Text>
      {plan.id === 'profissional' && <Badge colorScheme="brand">Popular</Badge>}
    </HStack>
    <Heading size="md" mb={3}>R$ {plan.price_brl?.toLocaleString('pt-BR')}<Text as="span" fontSize="sm" color="gray.500">/mês</Text></Heading>
    <List spacing={1} fontSize="sm">
      <ListItem><ListIcon as={CheckCircleIcon} color="green.500" />{plan.max_users} usuários</ListItem>
      <ListItem><ListIcon as={CheckCircleIcon} color="green.500" />{plan.max_collections >= 1000 ? 'Bases ilimitadas' : `${plan.max_collections} bases`}</ListItem>
      <ListItem><ListIcon as={CheckCircleIcon} color="green.500" />Até {plan.max_documents?.toLocaleString('pt-BR')} documentos</ListItem>
      <ListItem><ListIcon as={CheckCircleIcon} color="green.500" />Arquivos de até {plan.max_file_mb} MB</ListItem>
      <ListItem><ListIcon as={CheckCircleIcon} color="green.500" />{connectorNames(plan.connectors)}</ListItem>
      <ListItem><ListIcon as={CheckCircleIcon} color="green.500" />{plan.premium_models ? 'Modelos premium (Claude/Gemini Pro)' : 'Modelos padrão'}</ListItem>
    </List>
  </Box>
);

const SubscribeModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const toast = useToast();
  const { data: plans = [] } = useQuery('public-plans', publicApi.plans, { staleTime: Infinity });
  const paid = plans.filter((p) => (p.price_brl || 0) > 0);
  const [plan, setPlan] = useState('essencial');
  const [doc, setDoc] = useState('');
  const [touched, setTouched] = useState(false);
  const [loading, setLoading] = useState(false);

  const docValid = isValidDoc(doc);
  const showDocError = touched && doc.length > 0 && !docValid;

  const subscribe = async () => {
    setTouched(true);
    if (!docValid) {
      toast({ title: 'CPF ou CNPJ inválido', description: 'Verifique os números informados.', status: 'warning', duration: 3000 });
      return;
    }
    setLoading(true);
    try {
      const r = await billingApi.subscribe(plan, doc.replace(/\D/g, ''));
      if (r.payment_link) {
        window.open(r.payment_link, '_blank');
        toast({ title: 'Assinatura criada', description: 'Abrimos o link de pagamento numa nova aba.', status: 'success', duration: 6000, isClosable: true });
        onClose();
      }
    } catch (e: any) {
      toast({ title: 'Falha ao assinar', description: e?.response?.data?.detail || 'Tente novamente', status: 'error', duration: 5000, isClosable: true });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="2xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Escolha seu plano</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={5}>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              {paid.map((p) => (
                <PlanCard key={p.id} plan={p} selected={plan === p.id} onSelect={() => setPlan(p.id)} />
              ))}
            </SimpleGrid>
            <FormControl isRequired isInvalid={showDocError}>
              <FormLabel fontSize="sm">CPF ou CNPJ do responsável pela cobrança</FormLabel>
              <Input
                placeholder="Apenas números"
                value={doc}
                onChange={(e) => setDoc(e.target.value)}
                onBlur={() => setTouched(true)}
              />
              <FormErrorMessage>CPF ou CNPJ inválido.</FormErrorMessage>
            </FormControl>
            <Text fontSize="xs" color="gray.500">
              Você será levado ao pagamento (Pix, boleto ou cartão). O plano é liberado automaticamente após a confirmação.
            </Text>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="brand" onClick={subscribe} isLoading={loading}>Continuar para o pagamento</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default SubscribeModal;
